import { Box, Loader, useMantineColorScheme } from '@mantine/core';
import { IconCheck, IconCloudUpload } from '@tabler/icons-react';
import { useState } from 'react';
import { BRAND_CONFIRM_GREEN } from '@/theme';

type FloatingProductSubmitButtonProps = {
  formId?: string;
  label: string;
  /** Shown when there is nothing new to save (edit flow). */
  idleLabel?: string;
  disabled?: boolean;
  loading?: boolean;
  /** When true, uses accent icon/color/text to signal unsaved changes ready to save. */
  hasPendingChanges?: boolean;
  /** When set, renders a button (not form submit) — used for batch create. */
  onClick?: () => void;
};

export function FloatingProductSubmitButton({
  formId,
  label,
  idleLabel,
  disabled = false,
  loading = false,
  hasPendingChanges = true,
  onClick,
}: FloatingProductSubmitButtonProps) {
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';
  const [hovered, setHovered] = useState(false);
  const [pressed, setPressed] = useState(false);

  const isReady = hasPendingChanges && !disabled && !loading;
  const isInteractive = isReady && !loading;
  const surfaceBg = isDark ? 'rgba(28, 30, 34, 0.88)' : 'rgba(255, 255, 255, 0.92)';
  const borderColor = isReady
    ? isDark
      ? 'rgba(122, 150, 56, 0.35)'
      : 'rgba(93, 119, 35, 0.28)'
    : isDark
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(15, 23, 42, 0.1)';
  const textColor = isReady ? (isDark ? '#ecfccb' : BRAND_CONFIRM_GREEN.dark) : isDark ? '#94a3b8' : '#64748b';

  const shadowRest = isReady
    ? isDark
      ? '0 12px 36px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(122, 150, 56, 0.2)'
      : '0 12px 32px rgba(93, 119, 35, 0.18), 0 0 0 1px rgba(93, 119, 35, 0.14)'
    : isDark
      ? '0 10px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.04)'
      : '0 10px 28px rgba(15, 23, 42, 0.08), 0 2px 8px rgba(15, 23, 42, 0.04)';

  const shadowHover = isDark
    ? '0 16px 48px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(122, 150, 56, 0.28)'
    : '0 16px 40px rgba(93, 119, 35, 0.22), 0 0 0 1px rgba(93, 119, 35, 0.22)';

  const { light, base, dark, deep } = BRAND_CONFIRM_GREEN;
  const displayLabel = isReady ? label : (idleLabel ?? label);

  return (
    <button
      type={onClick ? 'button' : 'submit'}
      form={onClick ? undefined : formId}
      disabled={disabled || loading || !hasPendingChanges}
      aria-label={displayLabel}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => {
        setHovered(false);
        setPressed(false);
      }}
      onMouseDown={() => isInteractive && setPressed(true)}
      onMouseUp={() => setPressed(false)}
      style={{
        position: 'fixed',
        right: 'max(24px, env(safe-area-inset-right, 0px))',
        bottom: 'max(24px, env(safe-area-inset-bottom, 0px))',
        zIndex: 250,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 14,
        padding: '7px 22px 7px 7px',
        margin: 0,
        border: `1px solid ${borderColor}`,
        borderRadius: 999,
        background: surfaceBg,
        backdropFilter: 'saturate(180%) blur(14px)',
        WebkitBackdropFilter: 'saturate(180%) blur(14px)',
        boxShadow: hovered && isInteractive ? shadowHover : shadowRest,
        cursor: isInteractive ? 'pointer' : 'not-allowed',
        opacity: disabled && !loading ? 0.5 : isReady ? 1 : 0.82,
        transform:
          pressed && isInteractive
            ? 'translateY(0) scale(0.98)'
            : hovered && isInteractive
              ? 'translateY(-3px)'
              : 'none',
        transition:
          'transform 0.18s cubic-bezier(0.34, 1.2, 0.64, 1), box-shadow 0.22s ease, opacity 0.2s ease, border-color 0.2s ease',
        fontFamily: 'inherit',
        outline: 'none',
        WebkitAppearance: 'none',
      }}
    >
      <Box
        aria-hidden
        style={{
          width: 46,
          height: 46,
          borderRadius: '50%',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: loading
            ? `linear-gradient(145deg, ${light} 0%, ${base} 45%, ${dark} 82%, ${deep} 100%)`
            : isReady
              ? `linear-gradient(145deg, ${light} 0%, ${base} 45%, ${dark} 82%, ${deep} 100%)`
              : isDark
                ? 'linear-gradient(145deg, #475569 0%, #334155 100%)'
                : 'linear-gradient(145deg, #cbd5e1 0%, #94a3b8 100%)',
          boxShadow: isReady
            ? '0 4px 14px rgba(93, 119, 35, 0.28), inset 0 1px 0 rgba(255, 255, 255, 0.28)'
            : 'none',
          transition: 'background 0.2s ease, box-shadow 0.2s ease',
        }}
      >
        {loading ? (
          <Loader size="sm" color="white" type="oval" />
        ) : isReady ? (
          <IconCloudUpload size={22} stroke={2.2} color="#fff" style={{ filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.12))' }} />
        ) : (
          <IconCheck size={22} stroke={2.5} color="#fff" style={{ filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.12))' }} />
        )}
      </Box>

      <span
        style={{
          fontSize: 15,
          fontWeight: isReady ? 700 : 600,
          letterSpacing: '-0.01em',
          color: textColor,
          lineHeight: 1.2,
          whiteSpace: 'nowrap',
          paddingRight: 2,
        }}
      >
        {displayLabel}
      </span>
    </button>
  );
}
